import { Extension } from "@tiptap/core";
import { Selection, Plugin } from "@tiptap/pm/state";
import { runInAction } from "mobx";

import { arrowToCollapsed } from "@/components/tiptap/collapse/arrowNavigation";
import { isCollapsedSelection } from "@/components/tiptap/collapse/blockCollapse";
import { isCollapsed } from "@/components/tiptap/collapse/utils";
import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { isCellSelection } from "@/components/tiptap/utils/isCellSelection";
import { findJumpLineTarget } from "@/models/cursor/jumpLine";
import { CURSOR_POSITION_CLASS_NAME, findCursor } from "@/models/cursor/moveUpDown";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { InlineNodeRange } from "@/models/SelectionModel";
import type { Editor } from "@tiptap/core";
import type { EditorState } from "@tiptap/pm/state";

interface ExpressionSelectionOptions {
  doc: DocumentModel;
}

export const ExpressionSelection = Extension.create<ExpressionSelectionOptions>({
  name: "expressionSelection",

  onSelectionUpdate({ editor }) {
    const { doc } = this.options;
    const { state } = editor;

    const { from, to, anchor, head } = state.selection;

    /**
     * Reset expression selection if:
     *  1. the selection is empty. For example, when we move cursor or click text.
     *  2. A collapsed block is node-selected as a single highlighted unit.
     *     Its range spans the inner expressions, but they're hidden — don't math-select them.
     */
    if (from === to || isCollapsedSelection(state.selection)) {
      if (!doc.editor.suggestions.pointerAnchor) {
        doc.selection.resetSelection();
      }
      return;
    }

    const direction = head > anchor ? "forward" : "backward";

    const selectionRangeKeys = new Set(doc.selection.inlineNodeRanges.keys());

    state.doc.nodesBetween(from, to, (node, pos) => {
      if (nodeIsExpression(node)) {
        const id = String(node.attrs.nodeId ?? "");
        if (id) {
          const wrapperNode = doc.nodes.get(id);
          if (wrapperNode?.is(WrapperNodeModel)) {
            let range: InlineNodeRange | undefined | false = doc.selection.isWrapperNodeSelected(
              wrapperNode.id,
              true,
            );
            if (pos !== from && pos + 1 !== to) {
              range = doc.selection.selectWrapperNode(wrapperNode, direction);
            } else if (!doc.selection.startSelectionPosition) {
              if (doc.selection.pointerClientX !== undefined) {
                // During pointer drag: set partial selection using mouse position.
                // No fallback to full selection — if cursor is at the edge, show nothing.
                range = doc.selection.setPartialSelectionAtPointer(wrapperNode, direction);
              } else if (!doc.selection.isWrapperNodeSelected(wrapperNode.id, true)) {
                // No active shift-selection or pointer drag: auto-select boundary expressions
                // regardless of direction (e.g. Cmd+A, Cmd+Shift+Left/Right).
                range = doc.selection.selectWrapperNode(wrapperNode, direction);
              }
            }

            if (range) {
              selectionRangeKeys.delete(range.key);
            }
            return false;
          }
        }
      }
      return true;
    });
    runInAction(() => {
      selectionRangeKeys.forEach((key) => {
        // Protect ranges set by the mousemove handler for adjacent expressions
        if (!doc.selection.pointerDragRangeKeys.has(key)) {
          doc.selection.inlineNodeRanges.delete(key);
        }
      });
    });
  },

  addProseMirrorPlugins() {
    const { doc } = this.options;

    // Capture-phase listener ensures pointerClientX is set before ProseMirror
    // fires onSelectionUpdate, and provides smooth updates even when ProseMirror's
    // selection doesn't change (e.g. mouse moving within an atom node).
    const onMouseMove = (e: MouseEvent) => {
      if (e.buttons > 0) {
        doc.selection.handlePointerDragMove(e);
      }
    };

    const onMouseUp = () => {
      doc.selection.handlePointerDragEnd();
    };

    return [
      new Plugin({
        view() {
          document.addEventListener("mousemove", onMouseMove, true);
          document.addEventListener("mouseup", onMouseUp);
          return {
            destroy() {
              document.removeEventListener("mousemove", onMouseMove, true);
              document.removeEventListener("mouseup", onMouseUp);
            },
          };
        },
      }),
    ];
  },

  addKeyboardShortcuts() {
    return {
      "Mod-a": () => {
        this.options.doc.selection.resetSelection();
        return this.editor.commands.selectDocument();
      },
      "Shift-ArrowUp": ({ editor }) => {
        const { selection } = editor.state;
        if (isCellSelection(selection)) {
          return false;
        }
        if (arrowToCollapsed(editor.view, "ArrowUp", true)) {
          return true;
        }
        const result = handleSelectVertical("up", this.options.doc, this.editor);
        if (result) scrollToSelectionHead(this.editor);
        return result;
      },
      "Shift-ArrowDown": ({ editor }) => {
        const { selection } = editor.state;
        if (isCellSelection(selection)) {
          return false;
        }
        if (arrowToCollapsed(editor.view, "ArrowDown", true)) {
          return true;
        }
        const result = handleSelectVertical("down", this.options.doc, this.editor);
        if (result) scrollToSelectionHead(this.editor);
        return result;
      },
      "Shift-ArrowLeft": ({ editor }) => {
        const { selection } = editor.state;
        if (isCellSelection(selection)) {
          return false;
        }
        if (arrowToCollapsed(editor.view, "ArrowLeft", true)) {
          return true;
        }
        return handleSelectHorizontal("left", this.options.doc, this.editor);
      },
      "Shift-ArrowRight": ({ editor }) => {
        const { selection } = editor.state;
        if (isCellSelection(selection)) {
          return false;
        }
        if (arrowToCollapsed(editor.view, "ArrowRight", true)) {
          return true;
        }
        return handleSelectHorizontal("right", this.options.doc, this.editor);
      },
    };
  },
});

function handleSelectHorizontal(direction: "left" | "right", doc: DocumentModel, editor: Editor) {
  doc.cursor.releaseLockLeft();
  const { state } = editor;
  const { selection } = state;
  const { from, to, anchor, head } = selection;
  const isRange = from !== to;

  let selectionDirection: "forward" | "backward" = direction === "right" ? "forward" : "backward";

  if (isRange) {
    selectionDirection = head > anchor ? "forward" : "backward";
  }

  let newHead = getSelectableHead(state, head, direction);
  let newAnchor = isRange ? anchor : head;

  let targetHead = direction === "right" ? head : head - 1;
  let targetNode = state.doc.nodeAt(targetHead);

  // When extending an existing range, the head can be parked on the far edge of a
  // fully-selected expression, so look at the opposite side to find it. A collapsed
  // cursor has no such expression to grow out of: looking at the opposite side would
  // grab the math node behind the cursor and select it instead of the adjacent text.
  if (!nodeIsExpression(targetNode) && isRange) {
    targetHead = direction === "right" ? head - 1 : head;
    targetNode = state.doc.nodeAt(targetHead);
  }

  // Use native selection if target selected node is not expression
  if (!nodeIsExpression(targetNode)) {
    return false;
  }

  const wrapperNodeId = targetNode.attrs.nodeId;
  const wrapperNode = doc.textNode.wrapperNodes.find((n) => n.id === wrapperNodeId);

  if (wrapperNode?.inlineNode && wrapperNode.inlineNode.nodes.length > 0) {
    const { inlineNode } = wrapperNode;

    const nodeSelection = doc.selection.rollSelection(inlineNode, direction, selectionDirection);

    if (nodeSelection) {
      const { node, anchor: nodeAnchor, head: nodeHead } = nodeSelection;
      // The selection has a zero width in the middle of the node, move cursor to that position.
      if (nodeAnchor === nodeHead && nodeAnchor > 0 && nodeAnchor < node.nodes.length) {
        doc.cursor.moveTo(node, nodeAnchor);
        newAnchor = targetHead;
        newHead = targetHead + 1;
      }
      // The selection has a zero width in the middle of the node, move cursor to that position.
      else if (nodeAnchor === nodeHead) {
        newHead = selectionDirection === "forward" ? targetHead : targetHead + 1;
      }
      // If selection is partially selected, make this node selected.
      else {
        newHead = selectionDirection === "forward" ? targetHead + 1 : targetHead;
      }
    }
    // Move outside of the inline node, so we need to move 1 symbol further.
    else if (direction === "right") {
      newHead = getSelectableHead(state, targetHead + 1, direction);
    } else if (direction === "left") {
      newHead = getSelectableHead(state, targetHead, direction);
    }
  }
  editor.commands.setTextSelection({
    from: newAnchor,
    to: newHead,
  });

  return true;
}

function getSelectableHead(state: EditorState, fromPos: number, direction: "left" | "right") {
  const clamped = Math.max(
    0,
    Math.min(direction === "right" ? fromPos + 1 : fromPos - 1, state.doc.content.size),
  );
  const pos = state.doc.resolve(clamped);

  const selection = Selection.findFrom(pos, direction === "right" ? 1 : -1, true);
  if (selection) {
    return selection.head;
  }
  return clamped;
}

function handleSelectVertical(
  direction: "up" | "down",
  doc: DocumentModel,
  editor: Editor,
): boolean {
  const { state, view } = editor;
  const { head, anchor } = state.selection;
  const { top, bottom: headBottom, left: headLeft } = view.coordsAtPos(head);
  let bottom = headBottom;
  let left = headLeft;

  // When the head is parked right after a collapsed run, its `coordsAtPos` is a zero-height point
  // sitting in the collapsed bar's column, which no longer lines up with the next block's text
  // column (the toggle gutter indents it).
  if (direction === "down" && top === bottom && isCollapsed(state.doc.resolve(head).nodeBefore)) {
    const nextInside = view.coordsAtPos(head + 1);
    if (nextInside.bottom > nextInside.top) {
      bottom = nextInside.top;
      left = view.coordsAtPos(anchor).left;
    }
  }

  // If head is on an expression node, calculate left from the math selection boundary
  // instead of the ProseMirror atom node position.
  const boundaryLeft = getExpressionSelectionLeft(head, state, doc);

  let returnedToStart = false;

  const result = doc.cursor.getMoveToOptions(
    { left: boundaryLeft ?? left, top: direction === "up" ? top : bottom },
    (_, lockedOptions) => {
      const target = findJumpLineTarget(direction, lockedOptions, doc, true);
      if (target) {
        if ("expressionPos" in target) {
          const { coords, expressionPos } = target;
          const cursor = findCursor(doc, direction, {
            ...coords,
            allowCurrent: true,
            onlyTopLevel: true,
          });
          if (cursor) {
            const { nodeId, index } = cursor;
            const startPos = doc.selection.startSelectionPosition;
            if (
              startPos &&
              nodeId === startPos.node.id &&
              (index === startPos.index || boundaryLeft === undefined)
            ) {
              doc.selection.resetSelection();
              doc.cursor.reset();
              doc.cursor.moveTo(startPos.node, startPos.index);
              editor.commands.setTextSelection({
                from: expressionPos,
                to: expressionPos + 1,
              });
              returnedToStart = true;
            } else {
              selectToCursor(nodeId, index, expressionPos, doc, editor);
            }
            return true;
          }
          return false;
        }
        if ("nodePos" in target) {
          editor.commands.setTextSelection({
            from: anchor,
            to: target.nodePos,
          });
          return true;
        }
      }
      return false;
    },
  );

  // When returning to start, release lockLeft so subsequent non-shift navigation
  // uses fresh coordinates instead of the stale value from the shift-selection.
  if (returnedToStart) {
    doc.cursor.releaseLockLeft();
  }

  return result;
}

/**
 * When head is on an expression node, get the left coordinate from the math selection
 * boundary element instead of from the ProseMirror atom node position.
 * If selected from the left (forward), returns the position after the last selected element.
 * If selected from the right (backward), returns the position before the first selected element.
 */
function getExpressionSelectionLeft(
  head: number,
  state: EditorState,
  doc: DocumentModel,
): number | undefined {
  let exprNode = state.doc.nodeAt(head);
  if (!nodeIsExpression(exprNode) && head > 0) {
    exprNode = state.doc.nodeAt(head - 1);
  }
  if (!exprNode || !nodeIsExpression(exprNode)) return undefined;

  const wrapperNodeId = String(exprNode.attrs.nodeId ?? "");
  if (!wrapperNodeId) return undefined;

  const wrapperNode = doc.nodes.get(wrapperNodeId);
  if (!wrapperNode?.is(WrapperNodeModel)) return undefined;

  const { inlineNode } = wrapperNode;
  if (!inlineNode) return undefined;

  const range = doc.selection.inlineNodeRanges.get(inlineNode.id);
  if (!range) return undefined;

  // Find the cursor-position element at the range head index
  const selector = `.${CURSOR_POSITION_CLASS_NAME}[data-node-id="${inlineNode.id}"][data-cursor-index="${range.head}"]`;
  const element = doc.editor.tiptap?.view.dom.querySelector<HTMLElement>(selector);
  if (!element) return undefined;

  const rect = element.getBoundingClientRect();
  const { cursorPosition } = element.dataset;
  return cursorPosition === "end" ? rect.right : rect.left;
}

function selectToCursor(
  nodeId: string,
  index: number,
  expressionPos: number,
  doc: DocumentModel,
  editor: Editor,
) {
  const { state } = editor;
  const { selection } = state;
  let { anchor } = selection;
  const selectionDirection = expressionPos > anchor ? "forward" : "backward";

  let newHead = selectionDirection === "forward" ? expressionPos + 1 : expressionPos;
  const node = doc.nodes.get(nodeId);

  if (node && node.is(InlineNodeModel)) {
    const startPos = doc.selection.startSelectionPosition;
    const sourceWrapperFromStart = startPos?.node.ancestors.find((n): n is WrapperNodeModel =>
      n.is(WrapperNodeModel),
    );
    const targetWrapper = node.parent?.is(WrapperNodeModel) ? node.parent : undefined;

    // When the head moves back to the starting expression, set the range
    // from the starting index to the cursor and wrap just this expression.
    if (startPos && sourceWrapperFromStart && sourceWrapperFromStart === targetWrapper) {
      doc.selection.setInlineNodeRangeBetween(startPos.node, startPos.index, node, index);
      // Clear ranges for other expressions (head-side expressions are no longer selected)
      for (const [key] of doc.selection.inlineNodeRanges) {
        if (key !== node.id && key !== startPos.node.id) {
          doc.selection.inlineNodeRanges.delete(key);
        }
      }
      editor.commands.setTextSelection({
        from: expressionPos,
        to: expressionPos + 1,
      });
      return;
    }

    const sourceWrapper = doc.selection.extendCrossExpressionSourceRange(selectionDirection);

    if (selectionDirection === "forward") {
      const range = doc.selection.setInlineNodeRangeBetween(node, 0, node, index);
      // Nothing is selected, don't include this node in the selection
      if (range?.anchor === range?.head) {
        newHead = expressionPos;
      }
    } else {
      const range = doc.selection.setInlineNodeRangeBetween(node, node.nodes.length, node, index, {
        inclusive: true,
      });
      // Nothing is selected, don't include this node in the selection
      if (range?.anchor === range?.head) {
        newHead = expressionPos + 1;
      }
    }

    // Place the anchor on the source wrapper's far edge so the tiptap
    // selection range fully covers the source atom. Otherwise
    // onSelectionUpdate's nodesBetween skips it and deletes its range.
    if (sourceWrapper) {
      let sourceExprPos: number | undefined;
      if (state.doc.nodeAt(anchor)?.attrs.nodeId === sourceWrapper.id) {
        sourceExprPos = anchor;
      } else if (anchor > 0 && state.doc.nodeAt(anchor - 1)?.attrs.nodeId === sourceWrapper.id) {
        sourceExprPos = anchor - 1;
      }
      if (sourceExprPos !== undefined) {
        anchor = selectionDirection === "forward" ? sourceExprPos : sourceExprPos + 1;
      }
    }
  }

  editor.commands.setTextSelection({
    from: anchor,
    to: newHead,
  });
}

const SCROLL_THRESHOLD = 100;

function scrollToSelectionHead(editor: Editor): void {
  const { head } = editor.state.selection;
  const coords = editor.view.coordsAtPos(head);

  if (coords.top < SCROLL_THRESHOLD) {
    window.scrollBy({
      top: coords.top - SCROLL_THRESHOLD,
      behavior: "instant",
    });
  } else if (coords.bottom > window.innerHeight - SCROLL_THRESHOLD) {
    window.scrollBy({
      top: coords.bottom - (window.innerHeight - SCROLL_THRESHOLD),
      behavior: "instant",
    });
  }
}
